import { TagRootProps } from '@chakra-ui/react';
import {
  ContractStatusEnum,
  CustomerPortalSessionData,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { lazy, Suspense, useState } from 'react';
import { FaRegCalendarAlt } from 'react-icons/fa';
import {
  MBox,
  MFlex,
  MIcon,
  MLink,
  MTag,
  MText,
} from '../../../../components/Monetize';

const LatestQuoteDrawer = lazy(() =>
  import('./LatestQuoteDrawer').then((mod) => ({
    default: mod.LatestQuoteDrawer,
  })),
);

interface ContractTermBannerProps {
  status: ContractStatusEnum;
  termStart: string;
  termEnd: string;
  latestQuoteId?: string | null;
  sessionData?: CustomerPortalSessionData;
}

function getStatusTagVariant(
  status: ContractStatusEnum,
): TagRootProps['variant'] {
  switch (status) {
    case ContractStatusEnum.ACTIVE:
      return 'green';
    case ContractStatusEnum.CANCELED:
      return 'red';
    case ContractStatusEnum.FINISHED:
      return 'gray';
    case ContractStatusEnum.PENDING:
      return 'blue';
    default:
      return 'gray';
  }
}

function formatStatusLabel(status: ContractStatusEnum): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

export const ContractTermBanner = ({
  status,
  termStart,
  termEnd,
  latestQuoteId,
  sessionData,
}: ContractTermBannerProps) => {
  const [isQuoteDrawerOpen, setQuoteDrawerOpen] = useState(false);
  const canShowQuote = Boolean(latestQuoteId && sessionData);

  return (
    <MBox
      bg="bg.surface"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.subtle"
      borderRadius="lg"
      px={4}
      py={3}
    >
      <MFlex
        alignItems="center"
        justifyContent="space-between"
        gap={3}
        flexWrap="wrap"
      >
        <MFlex alignItems="center" gap={3}>
          <MIcon as={FaRegCalendarAlt} color="tIndigo.base" boxSize={4} />
          <MBox>
            <MText fontSize="xs" color="fg.muted" lineHeight="short">
              Current contract term
            </MText>
            <MFlex alignItems="center" gap={2}>
              <MText
                fontSize="sm"
                fontWeight={600}
                color="fg.primary"
                lineHeight="short"
              >
                {toDateShort(termStart)} – {toDateShort(termEnd)}
              </MText>
              <MTag variant={getStatusTagVariant(status)} size="sm">
                {formatStatusLabel(status)}
              </MTag>
            </MFlex>
          </MBox>
        </MFlex>
        {canShowQuote && (
          <MLink
            as="button"
            type="button"
            onClick={() => setQuoteDrawerOpen(true)}
            color="fg.link"
            fontSize="sm"
            fontWeight={500}
            _hover={{ color: 'fg.linkHover' }}
          >
            View latest contract
          </MLink>
        )}
      </MFlex>
      {canShowQuote && isQuoteDrawerOpen && (
        <Suspense fallback={null}>
          <LatestQuoteDrawer
            isOpen
            onClose={() => setQuoteDrawerOpen(false)}
            sessionData={sessionData!}
            quoteId={latestQuoteId!}
          />
        </Suspense>
      )}
    </MBox>
  );
};
